/**
 * Locale-aware time formatting.
 *
 * Uses `Intl.RelativeTimeFormat` rather than hand-built English strings so the
 * Spanish build reads naturally ("hace 3 h", not a literal translation of "3h ago").
 * The active UI language is passed in by the caller.
 */

const DIVISIONS: { limit: number; amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { limit: 60, amount: 1, unit: 'second' },
  { limit: 3600, amount: 60, unit: 'minute' },
  { limit: 86400, amount: 3600, unit: 'hour' },
  { limit: 2592000, amount: 86400, unit: 'day' },
];

/** "3h ago" / "hace 3 h". Falls back to a locale date past a month. */
export function relativeTime(iso?: string, locale = 'en'): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';

  const seconds = (Date.now() - then) / 1000;
  if (seconds < 45) {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(0, 'second');
  }
  for (const { limit, amount, unit } of DIVISIONS) {
    if (seconds < limit) {
      return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-Math.floor(seconds / amount), unit);
    }
  }
  return new Date(iso).toLocaleDateString(locale);
}

/** Most recent timestamp a record carries, for sorting history newest-first. */
export function latestTime(...isos: (string | undefined)[]): number {
  return Math.max(...isos.map((t) => (t ? new Date(t).getTime() : 0)));
}
